import React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Center,
  Flex,
  HStack,
  IconButton,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { MdDragHandle, MdOutlineClose } from "react-icons/md";
import { useAtomValue } from "jotai";
import { TransactionsAtom } from "../../../store";
import { Position, Trade, TradeEx } from "../../../types";
import "./Sash.css";
import "./PositionDetails.css";
import { CustomTab, CustomTabList } from "../../../components/design_library";
import { NotesTimeline } from "../../../components/timeline/NotesTimeline";
import TradingView from "../../../components/tradingview/TradingView";
import TradesAgGrid from "./components/TradesAgGrid";
import { PositionAudit } from "../../audit/PositionAudit";
import SharePositions from "./components/SharePositions";
import { CashflowView } from "../../cashflows/cashflow-view/CashflowView";
import { useInstruments } from "../../../hooks";
import { Payoff } from "../../Payoff/Payoff";
import Big from "big.js";
import { track, EVENTS } from "../../../utils/analytics";

interface PositionDetailsViewProps {
  onClose: () => void;
  tradeIds: number[];
  cashflowIds: number[];
  params: any;
  positions: Position[];
}

export const PositionDetailsView = (props: PositionDetailsViewProps) => {
  const transactions = useAtomValue(TransactionsAtom);
  const [groupStatsAvailable, setGroupStatsAvailable] =
    useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState(0);

  const instruments = useInstruments();

  const positions: Position[] = useMemo(() => {
    if (props.params?.node?.group !== true && props.params?.node?.data) {
      // leaf selected
      return [props.params.node.data as Position];
    } else if (
      props.params?.node?.group === true &&
      (props.params?.node?.allLeafChildren?.length ?? 0) > 0
    ) {
      // group selected
      return props.params.node.allLeafChildren.map((child: any) => {
        return child.data as Position;
      });
    }
    return [];
  }, [props.params]);

  useEffect(() => {
    if (props?.params?.node?.aggData) {
      setGroupStatsAvailable(true);
    } else {
      setGroupStatsAvailable(false);
    }
  }, [props.params]);

  const trades: TradeEx[] = useMemo(() => {
    const filteredTrades = transactions.trades.filter(
      (transaction: Trade) =>
        transaction.id && props.tradeIds?.includes(transaction.id)
    );

    const result = filteredTrades.map((trade) => {
      return {
        ...trade,
        instrument: trade.instrumentId
          ? instruments.list.find((x) => x.id === trade.instrumentId)
          : undefined,
        underlyingInstrument: trade.underlyingInstrumentId
          ? instruments.list.find((x) => x.id === trade.underlyingInstrumentId)
          : instruments.list.find((x) => x.id === trade.instrumentId),
      } as TradeEx;
    });

    return result;
  }, [transactions.trades, props.tradeIds, instruments.list]);

  const topLeftRef = useRef(null);

  useEffect(() => {
    if (transactions.trades.length > 0) {
      instruments.loadIds(
        transactions.trades.map((trade) => trade.instrumentId)
      );
    }
  }, [transactions.trades]);

  useEffect(() => {
    if (positions && positions.length > 0) {
      // Track position details loaded event
      track(EVENTS.POSITION.POSITION_DETAIL_LOADED, {
        position_count: positions.length,
        is_group: positions.length > 1,
        position_ids: positions.map((pos) => pos.id),
      });
    }
  }, [positions]);

  const onTabChange = (index: number) => {
    setTabIndex(index);

    // Track tab change event
    const tabNames = [
      "trades",
      "cashflows",
      "chart",
      "payoff",
      "timeline",
      "audit",
      "share",
    ];
    track(EVENTS.POSITION.POSITION_TAB_CHANGED, {
      tab_name: tabNames[index] || `tab-${index}`,
      position_count: positions.length,
      is_group: positions.length > 1,
    });
  };

  return (
    <Tabs
      className="position-view-tabs"
      {...props}
      mt="2"
      isLazy
      colorScheme={"blue"}
      onChange={onTabChange}
    >
      <Center>
        <Text flex="2">
          <MdDragHandle className="grabHandleOnPane" />
        </Text>
      </Center>

      <HStack
        w="full"
        justifyContent={"space-between"}
        alignItems={"flex-start"}
      >
        <CustomTabList flex="1" p={0}>
          <HStack justifyContent={"space-between"} w="100%">
            <HStack justifyContent={"space-between"}>
              <HStack>
                <CustomTab>Trades ({trades?.length ?? 0})</CustomTab>
                <CustomTab>
                  Cashflows ({props.cashflowIds?.length ?? 0})
                </CustomTab>
                <CustomTab>Historical Chart</CustomTab>
                <CustomTab>Payoff Chart</CustomTab>
                <CustomTab>Timeline</CustomTab>

                <CustomTab isDisabled={positions.length === 0}>
                  <Tooltip
                    label={
                      !props.params?.node?.data
                        ? "Audit is only available on single positions"
                        : ""
                    }
                  >
                    <Text>Audit</Text>
                  </Tooltip>
                </CustomTab>
                <CustomTab isDisabled={!props.params?.node?.data}>
                  <Text>Share</Text>
                </CustomTab>
              </HStack>
            </HStack>
          </HStack>
        </CustomTabList>

        <Box maxW="60px">
          <IconButton
            justifyContent={"flex-end"}
            pr="20px"
            flex="1"
            size="lg"
            height="24px"
            variant="none"
            icon={<MdOutlineClose />}
            aria-label="close"
            onClick={props.onClose}
            ref={topLeftRef}
          />
        </Box>
      </HStack>
      <TabPanels className="position-view-tabpanels" p={0}>
        <TabPanel p="0" h="full">
          <Flex className="position-trades" height="100%">
            <TradesAgGrid trades={trades} topLeftRef={topLeftRef} />
          </Flex>
        </TabPanel>

        <TabPanel p={0} m={0} h="full">
          <CashflowView cashflowIds={props.cashflowIds} />
        </TabPanel>

        <TabPanel p={0} m={0} h="full">
          <TradingView trades={trades} />
        </TabPanel>
        <TabPanel p={0} m={0} h="full">
          <Box p={0} m={0} height={"100%"}>
            <Payoff positions={positions} />
          </Box>
        </TabPanel>

        <TabPanel p={0} m={0} h="full">
          <NotesTimeline trades={trades} />
        </TabPanel>
        <TabPanel p={0} m={0} w="full" h="full">
          <PositionAudit trades={trades} positions={positions} />
        </TabPanel>

        <TabPanel p={0} m={0} h="full">
          <Box p={0} m={0} height={"100%"}>
            <SharePositions data={props?.params?.node?.data} trades={trades} />
          </Box>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
